<script setup lang="ts">
import type { InsightConfig, InsightConfigMeta } from '../../../models/insightConfig.types'

import { useI18n } from '@/shared/i18n'
import { Input } from '@/shared/ui/input'
import { Select } from '@/shared/ui/select'

const form = defineModel<InsightConfig>('form', { required: true })
const props = defineProps<{
  meta: InsightConfigMeta
  errors: Record<string, string[]>
  disabled?: boolean
}>()
const { t } = useI18n()

function update<K extends keyof InsightConfig>(field: K, value: InsightConfig[K]): void {
  form.value = { ...form.value, [field]: value }
}
function errorFor(field: keyof InsightConfig): string {
  return (props.errors[String(field)] ?? [])
    .map((message) => (message.startsWith('insights.') ? t(message) : message))
    .join(' ')
}
</script>

<template>
  <fieldset class="config-section grid gap-md min-w-0 p-lg border border-border rounded-lg">
    <legend>{{ t('insights.sysadmin.config.section.overview') }}</legend>
    <div class="config-section__grid grid grid-cols-2 gap-md max-md:grid-cols-[minmax(0,_1fr)]">
      <Input
        :model-value="form.enum"
        :label="t('insights.sysadmin.config.fields.enum')"
        :disabled="disabled"
        :error="Boolean(errorFor('enum'))"
        :error-message="errorFor('enum')"
        @update:model-value="update('enum', String($event))"
      />
      <Input
        :model-value="form.label"
        :label="t('insights.sysadmin.config.fields.label')"
        :disabled="disabled"
        :error="Boolean(errorFor('label'))"
        :error-message="errorFor('label')"
        @update:model-value="update('label', String($event))"
      />
      <Select
        :model-value="form.status"
        :label="t('insights.sysadmin.config.fields.status')"
        :options="meta.status_choices"
        :disabled="disabled"
        @update:model-value="$event !== null && update('status', $event)"
      />
      <Input
        :model-value="form.priority"
        type="number"
        :step="1"
        :label="t('insights.sysadmin.config.fields.priority')"
        :disabled="disabled"
        :error="Boolean(errorFor('priority'))"
        :error-message="errorFor('priority')"
        @update:model-value="update('priority', Number($event))"
      />
      <Input
        :model-value="form.default_refresh_interval_seconds"
        type="number"
        :min="1"
        :step="1"
        :label="t('insights.sysadmin.config.fields.refreshSeconds')"
        :disabled="disabled"
        :error="Boolean(errorFor('default_refresh_interval_seconds'))"
        :error-message="errorFor('default_refresh_interval_seconds')"
        @update:model-value="update('default_refresh_interval_seconds', Number($event))"
      />
    </div>
    <p class="config-section__hint m-0 text-muted text-[0.75rem]">
      {{ t('insights.sysadmin.config.refreshSecondsHint') }}
    </p>
  </fieldset>
</template>

<style scoped>
.config-section legend {
  padding-inline: var(--ui-space-xs);
  font-weight: 600;
}
</style>
